import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { TIER_LABELS } from "@/lib/api";
import { ChevronDown, Download, FileSpreadsheet, KeyRound, Layers, LogOut, Printer, Settings, ShieldCheck, Upload, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { useState } from "react";
import { PasswordDialog } from "@/components/PasswordDialog";

export function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { push } = useToast();
  const [pwOpen, setPwOpen] = useState(false);

  if (!user) return null;
  // first / reset password: nothing but the forced ตั้งรหัสผ่านใหม่ dialog (the server answers 403 must_change anyway)
  if (user.mustChange) return <div className="min-h-screen bg-background"><PasswordDialog open forced onOpenChange={() => {}} /></div>;

  const isAdminRoute = loc.pathname.startsWith("/admin");
  const visibleTiers = user.tiers;

  async function downloadXlsx() {
    try {
      const r = await fetch("/api/export/xlsx", { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BBT_Prices_${user!.username}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      push({ title: "ดาวน์โหลดไม่สำเร็จ", description: e.message, variant: "error" });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-beijer-dark text-white shadow-panel bg-pinstripe">
        <div className="bg-beijer">
          <div className="container flex h-16 items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 transition-opacity duration-300 hover:opacity-90">
              <img src="/logos/beijer-bgrimm-white.png" alt="Beijer B Grimm" className="h-9 w-auto" />
              <div className="hidden flex-col leading-tight sm:flex">
                <span className="font-display text-[11px] font-light uppercase tracking-[0.18em] opacity-80">BBT · พอร์ทัลราคาขาย</span>
                <span className="font-display text-base font-bold tracking-wide">Master Sale Price · 2026</span>
              </div>
            </Link>
            <div className="flex items-center gap-2">
              {user.role === "admin" && (<>
                <Button variant="ghost" size="sm" className="hidden text-white hover:bg-white/10 sm:inline-flex" onClick={downloadXlsx}>
                  <Download className="h-4 w-4" /> ดาวน์โหลด xlsx
                </Button>
                <Button variant="ghost" size="sm" className="hidden text-white hover:bg-white/10 sm:inline-flex" onClick={() => nav(`/print/${visibleTiers[0] ?? "license"}`)}>
                  <Printer className="h-4 w-4" /> พิมพ์ PDF
                </Button>
              </>)}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                    <span className="hidden sm:inline">{user.username}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user.role === "admin" ? "แอดมิน" : "ผู้ใช้"} · {user.username}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>ระดับราคาที่ดูได้</DropdownMenuLabel>
                  <div className="flex flex-wrap gap-1 px-3 pb-2">
                    {visibleTiers.map((t) => (
                      <Badge key={t} variant={t}>{TIER_LABELS[t]}</Badge>
                    ))}
                  </div>
                  <DropdownMenuSeparator />
                  {user.role === "admin" && (<>
                    <DropdownMenuItem onClick={downloadXlsx}>
                      <FileSpreadsheet className="h-4 w-4" /> ดาวน์โหลด xlsx
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav(`/print/${visibleTiers[0] ?? "license"}`)}>
                      <Printer className="h-4 w-4" /> พิมพ์ / PDF
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>)}
                  {/* an SQ2.0 login changes its one BBT password here too (server → SQ2.0 setPassword) */}
                  {/* บัญชีของฉัน: own row only (user-mgmt ADDENDUM 2) — managers/admins have the full ผู้ใช้ tab */}
                  {!(user.role === "admin" || user.usersAdmin) && (
                    <DropdownMenuItem onClick={() => nav("/account")}>
                      <Users className="h-4 w-4" /> บัญชีของฉัน
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => setPwOpen(true)}>
                    <KeyRound className="h-4 w-4" /> เปลี่ยนรหัสผ่าน
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }}>
                    <LogOut className="h-4 w-4" /> ออกจากระบบ
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <div className="bg-accent-orange" style={{ height: "2px" }} />
        {(user.role === "admin" || user.usersAdmin) && (
          <nav className="border-b border-white/10 bg-beijer-dark/95 backdrop-blur">
            <div className="container flex h-12 items-center gap-1 overflow-x-auto text-sm font-semibold">
              <NavTab to="/" icon={<Layers className="h-4 w-4" />} label="ดูราคา" exact />
              <span className="ml-3 mr-1 text-[10px] uppercase tracking-[0.18em] text-white/40">แอดมิน</span>
              <NavTab to="/admin/users" icon={<Users className="h-4 w-4" />} label="ผู้ใช้" />
              {user.role === "admin" && (<>
                <NavTab to="/admin/percentages" icon={<Settings className="h-4 w-4" />} label="ส่วนลด" />
                <NavTab to="/admin/catalog" icon={<FileSpreadsheet className="h-4 w-4" />} label="แค็ตตาล็อก" />
                <NavTab to="/admin/upload" icon={<Upload className="h-4 w-4" />} label="อัปโหลด" />
                <NavTab to="/admin/audit" icon={<ShieldCheck className="h-4 w-4" />} label="บันทึก" />
              </>)}
            </div>
          </nav>
        )}
      </header>
      <main className={cn("container py-6", isAdminRoute ? "pb-20" : "")}>
        <Outlet />
      </main>
      <PasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <footer className="border-t border-border py-6 text-center text-xs text-muted-foreground">
        Beijer B Grimm Thailand · พอร์ทัลราคาขาย · สำหรับใช้งานภายในเท่านั้น
      </footer>
    </div>
  );
}

function NavTab({ to, icon, label, exact }: { to: string; icon: React.ReactNode; label: string; exact?: boolean }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        cn(
          "flex h-9 items-center gap-2 rounded-md px-3 text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white",
          isActive && "bg-accent-orange/20 text-accent-orange",
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
